import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import api from '@/api/axios.js';
import { authApi } from '@/api/index.js';

export const useAuthStore = defineStore('auth', () => {
  const token = ref(localStorage.getItem('token') || null);
  const user  = ref(JSON.parse(localStorage.getItem('user') || 'null'));

  function normalizeBaseUrl(value) {
    const raw = String(value || '').trim();
    if (!raw) return '/api';
    return raw.replace(/\/+$/, '');
  }

  function isSafeBaseUrl(value) {
    const normalized = normalizeBaseUrl(value);
    if (normalized.startsWith('/')) return true;

    const envNormalized = normalizeBaseUrl(import.meta.env.VITE_API_BASE_URL || '');
    if (envNormalized && normalized === envNormalized) return true;

    if (typeof window === 'undefined') return false;
    try {
      const candidate = new URL(normalized, window.location.origin);
      return candidate.origin === window.location.origin;
    } catch {
      return false;
    }
  }

  function buildLoginApiCandidates() {
    const candidates = [];
    const push = (value) => {
      const normalized = normalizeBaseUrl(value);
      if (!candidates.includes(normalized)) candidates.push(normalized);
    };

    const envApiBase = import.meta.env.VITE_API_BASE_URL || '';
    const persistedApiBase = localStorage.getItem('apiBaseUrl') || '';

    // Wenn VITE_API_BASE_URL gesetzt ist (Production), direkt damit starten
    // – same-origin /api würde auf Cloudflare Pages landen (405)
    if (envApiBase) {
      push(envApiBase);
    } else {
      push('/api');
      if (typeof window !== 'undefined') {
        push(`${window.location.origin}/api`);
      }
    }
    if (isSafeBaseUrl(api.defaults.baseURL || '')) push(api.defaults.baseURL || '');
    if (isSafeBaseUrl(persistedApiBase)) push(persistedApiBase);

    // Alte oder unsichere Persistenz bereinigen (z. B. api.pages.dev-Guesses).
    if (persistedApiBase && !isSafeBaseUrl(persistedApiBase) && normalizeBaseUrl(persistedApiBase) !== normalizeBaseUrl(envApiBase)) {
      localStorage.removeItem('apiBaseUrl');
    }

    return candidates;
  }

  function shouldTryNextCandidate(error) {
    const status = Number(error?.response?.status || 0);
    return status === 404 || status === 405;
  }

  const isLoggedIn   = computed(() => !!token.value);
  const tenantId     = computed(() => user.value?.tenant?.id);
  const tenantTrade  = computed(() =>
    (user.value?.tenant?.trade || user.value?.trade || null)?.toLowerCase() ?? null
  );

  // Rollen-Shortcuts (für Rückwärtskompatibilität)
  const isAdmin   = computed(() =>
    ['admin', 'tenant_admin', 'superadmin'].includes(user.value?.role)
  );
  const isManager = computed(() =>
    ['admin', 'tenant_admin', 'superadmin', 'manager'].includes(user.value?.role)
  );
  const isSuperadmin = computed(() => user.value?.role === 'superadmin');

  /**
   * Prüft ob der eingeloggte User eine bestimmte Permission hat.
   * Superadmin hat immer alle Permissions.
   *
   * Verwendung:
   *   v-if="auth.can('projects.create')"
   *   v-if="auth.can('documents.view')"
   */
  function can(perm) {
    if (!user.value) return false;
    if (user.value.role === 'superadmin') return true;
    return user.value.permissions?.includes(perm) ?? false;
  }

  /**
   * Prüft ob der User mindestens eine der Permissions hat.
   *   v-if="auth.canAny('projects.view', 'customers.view')"
   */
  function canAny(...perms) {
    return perms.some(p => can(p));
  }

  async function login(email, password, slug) {
    const candidates = buildLoginApiCandidates();
    let lastError = null;
    let data = null;

    for (const baseURL of candidates) {
      try {
        const res = await api.post('/auth/login', { email, password, slug }, { baseURL });
        data = res.data;
        api.defaults.baseURL = baseURL;
        localStorage.setItem('apiBaseUrl', baseURL);
        break;
      } catch (err) {
        lastError = err;
        if (!shouldTryNextCandidate(err)) {
          throw err;
        }
      }
    }

    if (!data) {
      throw lastError || new Error('Login fehlgeschlagen');
    }

    token.value = data.token;
    user.value  = data.user;
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
  }

  function logout() {
    token.value = null;
    user.value  = null;
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
  }

  async function changePassword(oldPassword, newPassword) {
    await api.post('/auth/change-password', { oldPassword, newPassword });
  }

  if (token.value) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token.value}`;
  }

  const persistedApiBase = localStorage.getItem('apiBaseUrl') || '';
  if (persistedApiBase && isSafeBaseUrl(persistedApiBase)) {
    api.defaults.baseURL = normalizeBaseUrl(persistedApiBase);
  } else if (persistedApiBase) {
    localStorage.removeItem('apiBaseUrl');
  }

  // Signal um eine Tour View-übergreifend zu starten (z.B. Nav-Tour nach Dashboard-Tour)
  const pendingTourKey = ref(null);
  function requestTour(key) { pendingTourKey.value = key; }
  function consumeTour(key) {
    if (pendingTourKey.value === key) pendingTourKey.value = null;
  }

  function hasTourSeen(key) {
    return !!(user.value?.preferences?.tours_seen ?? []).includes(key);
  }

  async function markTourSeen(key) {
    if (!user.value) return;
    const current = user.value.preferences?.tours_seen ?? [];
    if (current.includes(key)) return;
    const updated = [...current, key];
    // Optimistisch im Store aktualisieren
    user.value = { ...user.value, preferences: { ...user.value.preferences, tours_seen: updated } };
    localStorage.setItem('user', JSON.stringify(user.value));
    try {
      // Atomarer Append serverseitig (statt vollständiges Array zu senden) –
      // verhindert Lost-Updates, wenn mehrere Touren kurz hintereinander abgeschlossen werden
      await authApi.markTourSeen(key);
    } catch {
      // Fehlschlag ignorieren – Tour wird beim nächsten Login erneut gezeigt
    }
  }

  return {
    token, user, isLoggedIn, tenantId, tenantTrade,
    isAdmin, isManager, isSuperadmin,
    can, canAny,
    login, logout, changePassword,
    hasTourSeen, markTourSeen,
    pendingTourKey, requestTour, consumeTour,
  };
});